// ============================================
// ConverseTech Frontend - Gemini Live Client
// ============================================
// Direct browser connection to Gemini Live API

import {
  GEMINI_CONFIG,
  AUDIO_CONFIG,
  type TrainingMode,
  type GeminiFunctionCall,
  type GeminiToolDefinition,
  getToolsForMode,
  formatToolsForGemini,
} from '@conversetech/shared';

export type GeminiSessionState =
  | 'disconnected'
  | 'connecting'
  | 'connected'
  | 'listening'
  | 'processing'
  | 'speaking'
  | 'error';

export interface GeminiLiveCallbacks {
  onStateChange: (state: GeminiSessionState) => void;
  onTranscript: (role: 'user' | 'assistant', text: string) => void;
  onAudioData: (data: ArrayBuffer) => void;
  onError: (error: Error) => void;
  onFunctionCall?: (functionCall: GeminiFunctionCall) => void;
}

export class GeminiLiveClient {
  private ws: WebSocket | null = null;
  private state: GeminiSessionState = 'disconnected';
  private callbacks: GeminiLiveCallbacks;
  private apiKey: string;
  private systemPrompt: string;
  private mode: TrainingMode;
  private tools: GeminiToolDefinition[];
  private isSetupComplete = false;
  private pendingAudio: string[] = [];
  private isIntentionalDisconnect = false;

  constructor(
    apiKey: string,
    systemPrompt: string,
    callbacks: GeminiLiveCallbacks,
    mode: TrainingMode = 'conversation'
  ) {
    this.apiKey = apiKey;
    this.systemPrompt = systemPrompt;
    this.callbacks = callbacks;
    this.mode = mode;
    this.tools = getToolsForMode(mode);
  }

  async connect(): Promise<void> {
    if (this.ws?.readyState === WebSocket.OPEN) {
      return;
    }

    this.isIntentionalDisconnect = false;
    this.setState('connecting');

    return new Promise((resolve, reject) => {
      const url = `${GEMINI_CONFIG.WEBSOCKET_URL}?key=${this.apiKey}`;

      this.ws = new WebSocket(url);
      this.ws.binaryType = 'arraybuffer';

      const timeout = setTimeout(() => {
        if (this.ws?.readyState !== WebSocket.OPEN) {
          this.ws?.close();
          this.setState('error');
          reject(new Error('Connection timeout'));
        }
      }, 10000);

      this.ws.onopen = () => {
        clearTimeout(timeout);
        this.sendSetupMessage();
      };

      this.ws.onmessage = (event) => {
        const wasConnecting = this.state === 'connecting';
        this.handleMessage(event.data);
        if (wasConnecting && this.state === 'connected') {
          resolve();
        }
      };

      this.ws.onerror = (event) => {
        clearTimeout(timeout);
        // Ignore errors from intentional disconnection
        if (this.isIntentionalDisconnect) {
          return;
        }
        console.error('Gemini Live: WebSocket error', event);
        const error = new Error('WebSocket connection error');
        this.callbacks.onError(error);
        if (this.state === 'connecting') {
          reject(error);
        }
      };

      this.ws.onclose = (event) => {
        clearTimeout(timeout);
        // Ignore close events from intentional disconnection
        if (this.isIntentionalDisconnect) {
          return;
        }
        this.isSetupComplete = false;

        if (this.state === 'connecting') {
          reject(new Error(`Connection failed: ${event.reason || event.code}`));
        }
        this.setState('disconnected');
      };
    });
  }

  disconnect(): void {
    this.isIntentionalDisconnect = true;
    this.setState('disconnected');
    if (this.ws) {
      // Clear handlers to prevent callbacks after intentional close
      this.ws.onopen = null;
      this.ws.onmessage = null;
      this.ws.onerror = null;
      this.ws.onclose = null;
      this.ws.close();
      this.ws = null;
    }
    this.isSetupComplete = false;
    this.pendingAudio = [];
  }

  sendAudio(data: ArrayBuffer): void {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
      return;
    }

    const base64 = this.arrayBufferToBase64(data);
    const message = JSON.stringify({
      realtimeInput: {
        mediaChunks: [
          {
            mimeType: `audio/pcm;rate=${AUDIO_CONFIG.INPUT_SAMPLE_RATE}`,
            data: base64,
          },
        ],
      },
    });

    if (this.isSetupComplete) {
      this.ws.send(message);
    } else {
      this.pendingAudio.push(message);
    }
  }

  /**
   * Send text content to Gemini for context injection.
   * Used by DET state machine to inject phase transition context.
   */
  sendClientContent(text: string): void {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
      return;
    }

    const message = JSON.stringify({
      clientContent: {
        turns: [{
          role: 'user',
          parts: [{ text }]
        }],
        turnComplete: true
      }
    });

    if (this.isSetupComplete) {
      this.ws.send(message);
    }
  }

  get sessionState(): GeminiSessionState {
    return this.state;
  }

  get isReady(): boolean {
    return this.state === 'connected' || this.state === 'listening';
  }

  private setState(state: GeminiSessionState): void {
    this.state = state;
    this.callbacks.onStateChange(state);
  }

  private sendSetupMessage(): void {
    const setupConfig: Record<string, unknown> = {
      model: GEMINI_CONFIG.MODEL,
      generationConfig: {
        responseModalities: ['AUDIO'],
        speechConfig: {
          voiceConfig: {
            prebuiltVoiceConfig: {
              voiceName: GEMINI_CONFIG.VOICE_NAME,
            },
          },
        },
      },
      systemInstruction: {
        parts: [{ text: this.systemPrompt }],
      },
      // Enable transcription for both input (user) and output (AI)
      inputAudioTranscription: {},
      outputAudioTranscription: {},
    };

    // Add tools if available for this mode
    if (this.tools.length > 0) {
      setupConfig.tools = [formatToolsForGemini(this.tools)];
    }

    const message = { setup: setupConfig };

    this.ws?.send(JSON.stringify(message));
  }

  private handleMessage(data: unknown): void {
    let text: string;

    if (typeof data === 'string') {
      text = data;
    } else if (data instanceof ArrayBuffer) {
      const decoder = new TextDecoder('utf-8');
      text = decoder.decode(data);
    } else {
      return;
    }

    try {
      const message = JSON.parse(text);
      this.processMessage(message);
    } catch (error) {
      console.error('Gemini Live: Failed to parse message', error);
    }
  }

  private processMessage(message: Record<string, unknown>): void {

    // Setup complete
    if ('setupComplete' in message) {
      this.isSetupComplete = true;
      this.setState('connected');
      // Flush pending audio
      for (const msg of this.pendingAudio) {
        this.ws?.send(msg);
      }
      this.pendingAudio = [];
      return;
    }

    // Check for inputTranscription at top level (some API versions)
    if ('inputTranscription' in message) {
      const transcription = message.inputTranscription as { text?: string };
      if (transcription?.text) {
        this.callbacks.onTranscript('user', transcription.text);
      }
      return;
    }

    // Tool call from Gemini
    if ('toolCall' in message) {
      const toolCall = message.toolCall as {
        functionCalls?: Array<{
          name: string;
          args: Record<string, unknown>;
          id?: string;
        }>;
      };

      if (toolCall.functionCalls) {
        for (const fc of toolCall.functionCalls) {
          // Handle log_user_speech - our workaround for inputTranscription
          if (fc.name === 'log_user_speech' && fc.args.user_speech && typeof fc.args.user_speech === 'string') {
            this.callbacks.onTranscript('user', fc.args.user_speech);
            // Send response and continue (don't notify callback for this internal function)
            this.sendFunctionResponse(fc.id || fc.name, fc.name, { logged: true });
            continue;
          }

          // Extract user's original phrase from correction function calls
          if (fc.args.original_phrase && typeof fc.args.original_phrase === 'string') {
            this.callbacks.onTranscript('user', fc.args.original_phrase);
          }

          // Notify callback for UI display (skip internal functions)
          if (this.callbacks.onFunctionCall) {
            this.callbacks.onFunctionCall({
              name: fc.name,
              args: fc.args,
            });
          }

          // Send function response back to Gemini
          this.sendFunctionResponse(fc.id || fc.name, fc.name, { success: true });
        }
      }
      return;
    }

    // Server content
    if ('serverContent' in message) {
      const content = message.serverContent as {
        modelTurn?: { parts?: Array<{
          text?: string;
          inlineData?: { mimeType: string; data: string };
          functionCall?: { name: string; args: Record<string, unknown> };
        }> };
        turnComplete?: boolean;
        inputTranscription?: { text?: string };
        outputTranscription?: { text?: string };
      };

      // Handle input transcription (user speech)
      if (content.inputTranscription?.text) {
        this.callbacks.onTranscript('user', content.inputTranscription.text);
      }

      // Handle output transcription (AI speech)
      if (content.outputTranscription?.text) {
        this.callbacks.onTranscript('assistant', content.outputTranscription.text);
      }

      // Handle model response
      if (content.modelTurn?.parts) {
        for (const part of content.modelTurn.parts) {
          // Text response (fallback if no audio transcription)
          if (part.text) {
            this.callbacks.onTranscript('assistant', part.text);
          }

          // Audio response
          if (part.inlineData?.mimeType?.startsWith('audio/')) {
            this.setState('speaking');
            const audioData = this.base64ToArrayBuffer(part.inlineData.data);
            this.callbacks.onAudioData(audioData);
          }

          // Function call in model turn (alternative format)
          if (part.functionCall) {
            if (this.callbacks.onFunctionCall) {
              this.callbacks.onFunctionCall({
                name: part.functionCall.name,
                args: part.functionCall.args,
              });
            }
          }
        }
      }

      if (content.turnComplete) {
        this.setState('connected');
      }
    }
  }

  /**
   * Send a function response back to Gemini.
   */
  private sendFunctionResponse(id: string, name: string, response: Record<string, unknown>): void {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) return;

    const message = {
      toolResponse: {
        functionResponses: [{
          id,
          name,
          response,
        }],
      },
    };

    this.ws.send(JSON.stringify(message));
  }

  private arrayBufferToBase64(buffer: ArrayBuffer): string {
    const bytes = new Uint8Array(buffer);
    let binary = '';
    for (let i = 0; i < bytes.byteLength; i++) {
      binary += String.fromCharCode(bytes[i]);
    }
    return btoa(binary);
  }

  private base64ToArrayBuffer(base64: string): ArrayBuffer {
    const binary = atob(base64);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) {
      bytes[i] = binary.charCodeAt(i);
    }
    return bytes.buffer;
  }
}
